import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Copy, Mail, RefreshCw, XCircle } from "lucide-react";
import * as React from "react";

import type { AdminOrganizationInvite, OrgRole } from "@ando/core";
import { Badge, Button, Checkbox, cn, useToast } from "@ando/ui";

import {
  useAdminOrgInvites,
  useResendAdminOrgInvite,
  useRevokeAdminOrgInvite,
} from "@/features/admin/hooks/useAdminOrg";
import { ROLE_ICON, ROLE_LABEL } from "@/lib/roles";

import {
  ConfirmDialog,
  LoadingRow,
  PaginationControls,
  SearchInput,
  type SortDir,
  SortHeader,
  usePagination,
} from "./_shared";

/**
 * Invites tab — pending invites list with resend / revoke / copy-link
 * actions, search, sort, pagination, bulk-revoke.
 *
 * v1 reference: ``OrgInvitesTab.tsx`` (157 LoC).
 */

export interface OrgInvitesTabProps {
  orgId: string;
}

function compareInvites(
  a: AdminOrganizationInvite,
  b: AdminOrganizationInvite,
  col: string,
  dir: SortDir,
): number {
  if (dir === null) return 0;
  const sign = dir === "asc" ? 1 : -1;
  switch (col) {
    case "email":
      return a.email.localeCompare(b.email) * sign;
    case "role":
      return a.role.localeCompare(b.role) * sign;
    case "expires":
      return (Date.parse(a.expires_at) - Date.parse(b.expires_at)) * sign;
    case "created":
      return (Date.parse(a.created_at) - Date.parse(b.created_at)) * sign;
    default:
      return 0;
  }
}

export function OrgInvitesTab({ orgId }: OrgInvitesTabProps): React.ReactElement {
  const { i18n } = useLingui();
  const { toast } = useToast();
  const invitesQuery = useAdminOrgInvites(orgId);
  const resend = useResendAdminOrgInvite(orgId);
  const revoke = useRevokeAdminOrgInvite(orgId);

  const [search, setSearch] = React.useState("");
  const [sortCol, setSortCol] = React.useState<string | null>("created");
  const [sortDir, setSortDir] = React.useState<SortDir>("desc");
  const { page, setPage, pageSize, setPageSize } = usePagination(25);
  const [selected, setSelected] = React.useState<Set<string>>(new Set());
  const [confirmRevoke, setConfirmRevoke] = React.useState<AdminOrganizationInvite | null>(null);
  const [bulkRevoke, setBulkRevoke] = React.useState(false);

  const all = invitesQuery.data ?? [];
  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter((i) => i.email.toLowerCase().includes(q));
  }, [all, search]);
  const sorted = React.useMemo(() => {
    if (sortDir === null || sortCol === null) return filtered;
    return [...filtered].sort((a, b) => compareInvites(a, b, sortCol, sortDir));
  }, [filtered, sortCol, sortDir]);
  const paged = React.useMemo(
    () => sorted.slice((page - 1) * pageSize, page * pageSize),
    [sorted, page, pageSize],
  );
  const allOnPageSelected = paged.length > 0 && paged.every((i) => selected.has(i.id));

  const handleSort = (col: string, dir: SortDir): void => {
    setSortCol(dir === null ? null : col);
    setSortDir(dir);
  };

  const handleResend = (inv: AdminOrganizationInvite): void => {
    resend.mutate(inv.id, {
      onSuccess: () =>
        toast.success(i18n._(msg`Invito reinviato`), {
          description: i18n._(msg`Email inviata a ${inv.email}.`),
          duration: 5000,
        }),
      onError: (err) =>
        toast.error(i18n._(msg`Reinvio fallito`), {
          description: err instanceof Error ? err.message : i18n._(msg`Errore inatteso`),
        }),
    });
  };

  const handleRevokeConfirmed = (): void => {
    if (!confirmRevoke) return;
    const inv = confirmRevoke;
    revoke.mutate(inv.id, {
      onSuccess: () => {
        toast.success(i18n._(msg`Invito revocato`), {
          description: i18n._(msg`${inv.email} non potrà più accettare l'invito.`),
          duration: 5000,
        });
        setConfirmRevoke(null);
      },
      onError: (err) =>
        toast.error(i18n._(msg`Revoca fallita`), {
          description: err instanceof Error ? err.message : i18n._(msg`Errore inatteso`),
        }),
    });
  };

  const handleBulkRevokeConfirmed = (): void => {
    const ids = Array.from(selected);
    Promise.allSettled(ids.map((id) => revoke.mutateAsync(id))).then((results) => {
      const failed = results.filter((r) => r.status === "rejected").length;
      if (failed === 0) {
        toast.success(i18n._(msg`Inviti revocati`), {
          description: i18n._(msg`${ids.length} operazioni completate.`),
        });
      } else {
        const ok = ids.length - failed;
        toast.error(i18n._(msg`Operazione parziale`), {
          description: i18n._(msg`${ok} successi, ${failed} errori.`),
        });
      }
      setSelected(new Set());
      setBulkRevoke(false);
    });
  };

  const handleCopyLink = async (inv: AdminOrganizationInvite): Promise<void> => {
    const link = `${window.location.origin}/invite/${inv.id}`;
    try {
      await navigator.clipboard.writeText(link);
      toast.success(i18n._(msg`Link copiato`), {
        description: link,
        duration: 5000,
      });
    } catch {
      toast.error(i18n._(msg`Copia fallita`), {
        description: i18n._(msg`Il browser ha negato l'accesso agli appunti.`),
      });
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SearchInput
          value={search}
          onChange={setSearch}
          ariaLabel={i18n._(msg`Cerca invito`)}
          placeholder={i18n._(msg`Cerca per email…`)}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => invitesQuery.refetch()}
          disabled={invitesQuery.isFetching}
        >
          <RefreshCw
            size={13}
            aria-hidden
            className={cn("mr-1.5", invitesQuery.isFetching && "animate-spin")}
          />
          {i18n._(msg`Aggiorna`)}
        </Button>
      </div>

      {selected.size > 0 ? (
        <div
          className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-accent/40 bg-transparent px-3 py-2 text-[12.5px] text-ink-1"
          role="region"
          aria-label={i18n._(msg`Azioni di gruppo`)}
        >
          <span>
            <strong>{selected.size}</strong> {i18n._(msg`inviti selezionati`)}
          </span>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={() => setBulkRevoke(true)}
            >
              {i18n._(msg`Revoca selezionati`)}
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={() => setSelected(new Set())}>
              {i18n._(msg`Annulla`)}
            </Button>
          </div>
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-lg border border-hairline bg-card/55">
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="border-hairline border-b text-left">
              <th scope="col" className="w-10 px-3 py-2">
                <Checkbox
                  aria-label={i18n._(msg`Seleziona tutto`)}
                  checked={allOnPageSelected}
                  onChange={() => {
                    setSelected((prev) => {
                      const next = new Set(prev);
                      if (allOnPageSelected) {
                        paged.forEach((i) => next.delete(i.id));
                      } else {
                        paged.forEach((i) => next.add(i.id));
                      }
                      return next;
                    });
                  }}
                />
              </th>
              <th scope="col" className="px-3 py-2">
                <SortHeader
                  label={i18n._(msg`Email`)}
                  columnId="email"
                  activeColumn={sortCol}
                  direction={sortDir}
                  onSort={handleSort}
                />
              </th>
              <th scope="col" className="px-3 py-2">
                <SortHeader
                  label={i18n._(msg`Ruolo`)}
                  columnId="role"
                  activeColumn={sortCol}
                  direction={sortDir}
                  onSort={handleSort}
                />
              </th>
              <th scope="col" className="hidden px-3 py-2 md:table-cell">
                <SortHeader
                  label={i18n._(msg`Scade`)}
                  columnId="expires"
                  activeColumn={sortCol}
                  direction={sortDir}
                  onSort={handleSort}
                />
              </th>
              <th scope="col" className="hidden px-3 py-2 lg:table-cell">
                <SortHeader
                  label={i18n._(msg`Invitato`)}
                  columnId="created"
                  activeColumn={sortCol}
                  direction={sortDir}
                  onSort={handleSort}
                />
              </th>
              <th scope="col" className="px-3 py-2 text-right">
                <span className="font-semibold text-[10px] text-ink-3 uppercase tracking-wide">
                  {i18n._(msg`Azioni`)}
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {invitesQuery.isLoading ? (
              <tr>
                <td colSpan={6} className="px-3">
                  <LoadingRow label={i18n._(msg`Caricamento inviti…`)} />
                </td>
              </tr>
            ) : paged.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-[12px] text-ink-3">
                  <Mail className="mx-auto mb-2 h-5 w-5 opacity-40" aria-hidden />
                  {search ? i18n._(msg`Nessun risultato`) : i18n._(msg`Nessun invito in sospeso`)}
                </td>
              </tr>
            ) : (
              paged.map((inv) => {
                const isSel = selected.has(inv.id);
                const RoleGlyph = ROLE_ICON[inv.role];
                return (
                  <tr
                    key={inv.id}
                    className={cn(
                      "border-hairline/60 border-b last:border-b-0",
                      isSel ? "bg-ando-orange-soft/30" : "hover:bg-ando-orange-soft/20",
                    )}
                  >
                    <td className="h-8 px-3 py-2 align-middle">
                      <Checkbox
                        aria-label={i18n._(msg`Seleziona invito ${inv.email}`)}
                        checked={isSel}
                        onChange={(e) => {
                          setSelected((prev) => {
                            const next = new Set(prev);
                            if (e.target.checked) next.add(inv.id);
                            else next.delete(inv.id);
                            return next;
                          });
                        }}
                      />
                    </td>
                    <td className="h-8 px-3 py-2 align-middle font-medium text-ink-1">
                      {inv.email}
                    </td>
                    <td className="h-8 px-3 py-2 align-middle">
                      <span className="inline-flex items-center gap-1.5">
                        <RoleGlyph className="size-3.5" aria-hidden />
                        <span className="capitalize">
                          {i18n._(ROLE_LABEL[inv.role as OrgRole])}
                        </span>
                      </span>
                    </td>
                    <td className="hidden h-8 px-3 py-2 align-middle text-ink-3 md:table-cell">
                      {new Date(inv.expires_at).toLocaleDateString(i18n.locale)}
                    </td>
                    <td className="hidden h-8 px-3 py-2 align-middle text-ink-3 lg:table-cell">
                      {new Date(inv.created_at).toLocaleDateString(i18n.locale)}
                    </td>
                    <td className="h-8 px-3 py-2 text-right align-middle">
                      <div className="inline-flex items-center gap-1">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => handleResend(inv)}
                          aria-label={i18n._(msg`Reinvia invito a ${inv.email}`)}
                          disabled={resend.isPending}
                        >
                          <RefreshCw size={14} aria-hidden />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => handleCopyLink(inv)}
                          aria-label={i18n._(msg`Copia link invito a ${inv.email}`)}
                        >
                          <Copy size={14} aria-hidden />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10"
                          onClick={() => setConfirmRevoke(inv)}
                          aria-label={i18n._(msg`Revoca invito a ${inv.email}`)}
                        >
                          <XCircle size={14} aria-hidden />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <PaginationControls
        page={page}
        pageSize={pageSize}
        total={sorted.length}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />

      <ConfirmDialog
        open={confirmRevoke !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmRevoke(null);
        }}
        title={i18n._(msg`Revocare invito?`)}
        description={
          confirmRevoke ? (
            <>
              {i18n._(msg`L'invito a`)} <strong>{confirmRevoke.email}</strong>{" "}
              {i18n._(
                msg`non sarà più valido. Per re-invitare l'utente bisogna inviare un nuovo invito.`,
              )}
            </>
          ) : null
        }
        confirmLabel={revoke.isPending ? i18n._(msg`Revoca…`) : i18n._(msg`Revoca`)}
        destructive
        loading={revoke.isPending}
        onConfirm={handleRevokeConfirmed}
      />

      <ConfirmDialog
        open={bulkRevoke}
        onOpenChange={setBulkRevoke}
        title={i18n._(msg`Revocare gli inviti selezionati?`)}
        description={
          <>
            {i18n._(msg`Stai per revocare`)} <strong>{selected.size}</strong>{" "}
            {selected.size === 1 ? i18n._(msg`invito`) : i18n._(msg`inviti`)}.{" "}
            {i18n._(msg`I link diventano invalidi immediatamente.`)}
          </>
        }
        confirmLabel={revoke.isPending ? i18n._(msg`Revoca…`) : i18n._(msg`Revoca tutti`)}
        destructive
        loading={revoke.isPending}
        onConfirm={handleBulkRevokeConfirmed}
      />
    </div>
  );
}
